// Simple logging utility for the web app
class Logger {
    private isDevelopment = process.env.NODE_ENV === 'development';
    private crashLogs: Array<{timestamp: string, type: string, data: any}> = [];
    private maxCrashLogs = 50;

    constructor() {
        this.loadPersistedLogs();
        this.setupGlobalErrorHandlers();
    }

    private loadPersistedLogs() {
        try {
            const stored = localStorage.getItem('redo_crash_logs');
            if (stored) {
                this.crashLogs = JSON.parse(stored);
            }
        } catch (error) {
            console.warn('Failed to load persisted crash logs:', error);
        }
    }

    private persistLog(type: string, data: any) {
        const logEntry = {
            timestamp: new Date().toISOString(),
            type,
            data
        };
        
        this.crashLogs.unshift(logEntry);
        
        // Keep only the most recent logs
        if (this.crashLogs.length > this.maxCrashLogs) {
            this.crashLogs = this.crashLogs.slice(0, this.maxCrashLogs);
        }
        
        try {
            localStorage.setItem('redo_crash_logs', JSON.stringify(this.crashLogs));
        } catch (error) {
            console.warn('Failed to persist crash log:', error);
        }
    }

    private setupGlobalErrorHandlers() {
        // Catch unhandled JavaScript errors
        window.addEventListener('error', (event) => {
            const errorData = {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                message: event.message,
                stack: event.error?.stack,
                userAgent: navigator.userAgent,
                url: window.location.href
            };
            
            this.persistLog('javascript_error', errorData);
            this.error('Unhandled JavaScript Error:', event.error, errorData);
        });

        // Catch unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            const errorData = {
                reason: event.reason,
                promise: event.promise.toString(),
                userAgent: navigator.userAgent,
                url: window.location.href
            };
            
            this.persistLog('promise_rejection', errorData);
            this.error('Unhandled Promise Rejection:', event.reason, errorData);
        });

        // Log when the app starts
        this.info('Application initialized', { 
            userAgent: navigator.userAgent,
            url: window.location.href,
            timestamp: new Date().toISOString(),
            previousCrashes: this.crashLogs.length
        });
        
        // If we had previous crashes, log them
        if (this.crashLogs.length > 0) {
            this.warn(`Found ${this.crashLogs.length} previous crash logs`, {
                latestCrash: this.crashLogs[0]
            });
        }
    }

    private formatMessage(level: string, message: string, data?: any): string {
        const timestamp = new Date().toISOString();
        const dataStr = data ? ` | Data: ${JSON.stringify(data, null, 2)}` : '';
        return `[${timestamp}] [${level}] ${message}${dataStr}`;
    }

    info(message: string, data?: any) {
        if (this.isDevelopment) {
            console.log(this.formatMessage('INFO', message, data));
        }
    }

    warn(message: string, data?: any) {
        console.warn(this.formatMessage('WARN', message, data));
    }

    error(message: string, error?: any, data?: any) {
        const errorInfo = error ? {
            message: error.message,
            stack: error.stack,
            ...(error.response && { response: error.response })
        } : null;
        
        const errorData = { error: errorInfo, data };
        
        // Persist application errors too
        this.persistLog('application_error', { message, ...errorData });
        
        console.error(this.formatMessage('ERROR', message, errorData));
    }

    // Method to get crash logs for debugging
    getCrashLogs() {
        return [...this.crashLogs];
    }

    // Method to clear crash logs
    clearCrashLogs() {
        this.crashLogs = [];
        localStorage.removeItem('redo_crash_logs');
        this.info('Crash logs cleared');
    }

    // Method to export crash logs for support
    exportCrashLogs() {
        const exportData = {
            exported: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
            crashLogs: this.crashLogs
        };
        
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `redo-crash-logs-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        this.info('Crash logs exported');
    }

    debug(message: string, data?: any) {
        if (this.isDevelopment) {
            console.debug(this.formatMessage('DEBUG', message, data));
        }
    }

    // Log API requests and responses
    apiRequest(method: string, url: string, data?: any) {
        this.info(`API Request: ${method} ${url}`, data);
    }

    apiResponse(method: string, url: string, status: number, data?: any) {
        this.info(`API Response: ${method} ${url} - Status: ${status}`, data);
    }

    apiError(method: string, url: string, error: any) {
        this.error(`API Error: ${method} ${url}`, error);
    }
}

export const logger = new Logger();